import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CaretDown, CaretRight, MagnifyingGlass } from '@phosphor-icons/react';

/**
 * Phase 735.53 — Payment-focused Cleaner selector.
 *
 * The old dropdown treated Paid and Unpaid cleaners equally, which meant
 * the operator had to open a popover just to see who still owes money.
 * The redesign flips the emphasis:
 *
 *   ┌ UNPAID CLEANERS  ─────────────────────┐
 *   │  Shaly                       €223.00  │  ← main, always visible
 *   │  TONY                        €568.87  │
 *   │  Nats                        €773.00  │
 *   └───────────────────────────────────────┘
 *
 *   ▸ Paid cleaners (6)  ← secondary, collapsed by default
 *
 * The unpaid list is sorted by largest outstanding first, then A→Z.
 * Selecting any cleaner still calls `onSelect(cleaner)` so the detail
 * panel behaviour is unchanged.
 *
 * Backwards-compatible props: `getPaymentStatus`, `paymentStatusFilter`,
 * `onPaymentStatusFilterChange` are accepted but no longer consumed —
 * status is derived internally from `total_earned` / `total_paid`.
 */

const fmtMoney = (n) => `€${(Math.round(Number(n || 0) * 100) / 100).toFixed(2)}`;

const outstandingOf = (c) => {
  // Phase 735.54 — Use the statement-parity outstanding from the backend
  // (`statement_outstanding`) so this list matches the Statement Ledger
  // tab exactly. Older UI code paths that don't yet expose the field are
  // supported by the historical `total_earned − total_paid` fallback.
  if (c && Object.prototype.hasOwnProperty.call(c, 'statement_outstanding')) {
    return Math.round((Number(c.statement_outstanding) || 0) * 100) / 100;
  }
  const earned = Number(c?.total_earned || 0);
  const paid = Number(c?.total_paid || 0);
  return Math.round((earned - paid) * 100) / 100;
};

export const CleanerSelectorDropdown = ({
  cleaners,
  selectedCleanerId,
  onSelect,
  t,
}) => {
  const [paidOpen, setPaidOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef(null);

  const { unpaid, paid } = useMemo(() => {
    const list = Array.isArray(cleaners) ? cleaners : [];
    const q = query.trim().toLowerCase();
    const matches = (c) => !q || (c.name || '').toLowerCase().includes(q);
    const unpaid = [];
    const paid = [];
    for (const c of list) {
      if (!matches(c)) continue;
      const out = outstandingOf(c);
      // Phase 735.54 — Only truly-positive balances count as "unpaid".
      // Values ≤ 0 (settled or credit) go to the Paid section and the
      // displayed amount clamps to €0 to avoid misleading negatives on
      // the collapsed row.
      if (out > 0.005) {
        unpaid.push({ ...c, _outstanding: out });
      } else {
        paid.push({ ...c, _outstanding: Math.max(0, out) });
      }
    }
    // Unpaid: largest debt first, then A→Z for ties.
    unpaid.sort((a, b) => (b._outstanding - a._outstanding) || (a.name || '').localeCompare(b.name || ''));
    // Paid: pure A→Z, they are secondary.
    paid.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { unpaid, paid };
  }, [cleaners, query]);

  // Auto-expand paid when the user actively searches — otherwise the
  // paid matches would silently hide inside the collapsed section.
  useEffect(() => {
    if (query && paid.length > 0) setPaidOpen(true);
  }, [query, paid.length]);

  const totalOutstanding = useMemo(
    () => unpaid.reduce((s, c) => s + c._outstanding, 0),
    [unpaid]
  );

  const renderRow = (c, muted) => {
    const isSel = c.cleaner_id === selectedCleanerId;
    return (
      <button
        key={c.cleaner_id}
        type="button"
        onClick={() => onSelect(c)}
        data-testid={`cleaner-selector-option-${c.cleaner_id}`}
        aria-current={isSel ? 'true' : 'false'}
        className={`w-full text-left flex items-baseline justify-between gap-3 px-3 py-2 border-b border-zinc-100 last:border-b-0 transition ${
          isSel
            ? 'bg-zinc-900 text-white'
            : muted
              ? 'text-zinc-600 hover:bg-zinc-50'
              : 'text-zinc-900 hover:bg-zinc-50'
        }`}
      >
        <span className={`flex-1 min-w-0 truncate text-sm ${isSel ? 'font-semibold' : ''}`}>
          {c.name}
        </span>
        <span className={`font-mono text-sm shrink-0 tabular-nums ${
          isSel ? 'text-white' : (muted ? 'text-zinc-400' : 'text-zinc-900 font-semibold')
        }`}>
          {fmtMoney(c._outstanding)}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="cleaner-selector">
      {/* Search */}
      <div className="border border-zinc-300 flex items-center gap-2 px-2 py-1.5 bg-white">
        <MagnifyingGlass size={14} className="text-zinc-400" />
        <input
          ref={searchInputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('searchCleaner') || t('search') || 'Search cleaners…'}
          className="flex-1 text-sm outline-none bg-transparent placeholder:text-zinc-400"
          data-testid="cleaner-selector-search"
        />
      </div>

      {/* UNPAID — always visible main list */}
      <div className="border border-zinc-300 bg-white" data-testid="cleaner-selector-unpaid-section">
        <div className="flex items-baseline justify-between px-3 py-2 border-b border-zinc-300 bg-zinc-50">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-700"
                data-testid="cleaner-selector-unpaid-header">
            {t('unpaidCleaners') || 'Unpaid cleaners'}
            <span className="ml-1.5 text-zinc-400 font-mono normal-case tracking-normal">
              ({unpaid.length})
            </span>
          </span>
          {unpaid.length > 0 && (
            <span className="text-[11px] font-mono text-zinc-600" data-testid="cleaner-selector-unpaid-total">
              {fmtMoney(totalOutstanding)}
            </span>
          )}
        </div>
        {unpaid.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-zinc-400"
               data-testid="cleaner-selector-unpaid-empty">
            {query
              ? (t('noMatches') || 'No matches.')
              : (t('allCleanersPaid') || 'All cleaners are paid up.')}
          </div>
        ) : (
          <div className="max-h-[360px] overflow-y-auto" data-testid="cleaner-selector-unpaid-list">
            {unpaid.map(c => renderRow(c, false))}
          </div>
        )}
      </div>

      {/* PAID — secondary, collapsed */}
      <div className="border border-zinc-200 bg-white" data-testid="cleaner-selector-paid-section">
        <button
          type="button"
          onClick={() => setPaidOpen(o => !o)}
          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition"
          data-testid="cleaner-selector-paid-toggle"
          aria-expanded={paidOpen}
        >
          <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {t('paidCleaners') || 'Paid cleaners'}
            <span className="ml-1.5 text-zinc-400 font-mono normal-case tracking-normal">
              ({paid.length})
            </span>
          </span>
          {paidOpen
            ? <CaretDown size={14} className="text-zinc-400" />
            : <CaretRight size={14} className="text-zinc-400" />}
        </button>
        {paidOpen && (
          paid.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-zinc-400 border-t border-zinc-100"
                 data-testid="cleaner-selector-paid-empty">
              {query ? (t('noMatches') || 'No matches.') : (t('noCleaners') || 'No paid cleaners.')}
            </div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto border-t border-zinc-100"
                 data-testid="cleaner-selector-paid-list">
              {paid.map(c => renderRow(c, true))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CleanerSelectorDropdown;
